/**
 * @fileoverview Route guard for admin login page that redirects logged-in users
 * to their appropriate page based on role.
 */

import React, {ComponentType, useEffect} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";

import {isAllowedPathForItAdmin, getSsoConfigPath} from "./AdminHasRole";
import {RoleNameEnum} from "../../utils/types/common";
import {UserDto} from "../../utils/types/user";
import {select, AppState} from "../../store";


const DEFAULT_REDIRECT_PATH = "/admin/dashboard";

/**
 * Route guard for the admin login page.
 * Redirects logged-in users to their appropriate page based on role:
 * - ORGANIZATION_IT_ADMIN: SSO configuration page
 * - All other roles: Dashboard
 *
 * Also supports redirecting to the last visited page if available,
 * but only if the path is allowed for the user's role.
 */
export function AdminIsLoggedOut<OwnProps>(Component: ComponentType<OwnProps>) {
  return function Redirect(props: OwnProps) {
    const history = useHistory();
    const location = useLocation<{from?: string}>();

    const isLoggedIn = useSelector((state: AppState) =>
      select.adminSession.isLoggedIn(state),
    );
    const userRole = useSelector((state: AppState) =>
      select.adminSession.userRoleName(state),
    );
    const user = useSelector(
      (state: AppState) => select.adminSession.user(state) as UserDto | null,
    );

    const isItAdmin = userRole === RoleNameEnum.ORGANIZATION_IT_ADMIN;
    const organizationId = user?.organization?.id;

    useEffect(() => {
      if (!isLoggedIn) return;

      const fromPath = location.state?.from;

      // Check if we should go back to the previous page
      // For ORGANIZATION_IT_ADMIN, only go back if the path is allowed
      const canGoBack = fromPath && (!isItAdmin || isAllowedPathForItAdmin(fromPath));

      if (canGoBack) {
        history.goBack();
      } else {
        // Get role-appropriate redirect path
        const redirectPath =
          isItAdmin && organizationId
            ? getSsoConfigPath(organizationId)
            : DEFAULT_REDIRECT_PATH;

        history.push({
          pathname: redirectPath,
          state: {from: location.pathname},
        });
      }
    }, [isLoggedIn, isItAdmin, organizationId, history, location]);

    return !isLoggedIn
      ? React.createElement(Component, props as OwnProps)
      : null;
  };
}
